<template>
  <div class="relatorio-combos">
    <box :title="t('Combos por unidade')">
      <div class="rc-filtros" data-testid="relatorio-combos-filtros">
        <FranchiseSelect v-model="franchiseId" elem-id="relatorio-combos-franquia" :label="t('Franquia')" />
        <input-date elem-id="rc-data-inicio" data-testid="rc-data-inicio" :label="t('Data inicial')" v-model="dataInicio" />
        <input-date elem-id="rc-data-fim" data-testid="rc-data-fim" :label="t('Data final')" v-model="dataFim" />
        <app-button
          size="small"
          :label="t('Buscar')"
          no-explanation="aplica os filtros da tela"
          data-testid="rc-buscar"
          @click="buscar"
        />
      </div>

      <div v-if="erro" class="rc-aviso" data-testid="relatorio-combos-erro">{{ erro }}</div>

      <div v-else-if="relatorio && relatorio.combos.length === 0" class="rc-aviso" data-testid="relatorio-combos-vazio">
        {{ t('Nenhum combo vendido ou com saldo nesta unidade.') }}
      </div>

      <template v-else-if="relatorio">
        <div class="rc-totais" data-testid="relatorio-combos-totais">
          <StatsCard compact :label="t('Vendidos')" :value="relatorio.totais.vendidos ?? 0" icon="pi pi-shopping-cart" variant="primary" :explanation="EX.vendidos" />
          <StatsCard compact :label="t('Valor vendido')" :value="moeda(relatorio.totais.valor_vendido)" icon="pi pi-wallet" variant="success" :explanation="EX.valorVendido" />
          <StatsCard compact :label="t('Cancelados')" :value="relatorio.totais.cancelados ?? 0" icon="pi pi-ban" variant="danger" :explanation="EX.cancelados" />
          <StatsCard compact :label="t('Valor cancelado')" :value="moeda(relatorio.totais.valor_cancelado)" icon="pi pi-ban" variant="danger" :explanation="EX.valorCancelado" />
          <StatsCard compact :label="t('Pares usados')" :value="relatorio.totais.pares_usados ?? 0" icon="pi pi-check" variant="info" :explanation="EX.paresUsados" />
          <StatsCard compact :label="t('Saldo disponível')" :value="relatorio.totais.saldo_disponivel ?? 0" icon="pi pi-box" variant="success" :explanation="EX.saldoDisponivel" />
          <StatsCard compact :label="t('Saldo reservado')" :value="relatorio.totais.saldo_reservado ?? 0" icon="pi pi-clock" variant="warning" :explanation="EX.saldoReservado" />
          <StatsCard compact :label="t('Saldo vencido')" :value="relatorio.totais.saldo_vencido ?? 0" icon="pi pi-calendar-times" variant="warning" :explanation="EX.saldoVencido" />
        </div>

        <h4>{{ t('Por combo') }}</h4>
        <data-table test-id-prefix="relatorio-combos" :config="combosConfig" :data="relatorio.combos" :endpoint="null" />

        <h4>{{ t('Movimentos do período') }}</h4>
        <ComboMovimentosTable
          test-id-prefix="relatorio-combos-movimentos"
          :movimentos="relatorio.movimentos"
          :franchise-id="franchiseBuscada"
          mostra-cliente
        />
      </template>
    </box>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCombos, type RelatorioCombosUnidade } from '../../composables/useSOCombos';
import { useMoedaFranquia } from '../../composables/useMoedaFranquia';
import { useDateFormatter } from '../../composables/useDateFormatter';
import StatsCard from '../../ui/StatsCard.vue';
import { comboExplanations } from './explanations';
import ComboMovimentosTable from './ComboMovimentosTable.vue';

const { t } = useI18n();
const { fetchRelatorioUnidade } = useSOCombos();
const { formatarMoeda } = useMoedaFranquia();
const { fromDateToApiDate } = useDateFormatter();

const EX = computed(() => comboExplanations(t));

const hoje = new Date();
const franchiseId = ref<number | string | null>(null);
const dataInicio = ref<string>(fromDateToApiDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
const dataFim = ref<string>(fromDateToApiDate(hoje));

// A moeda da tela é a da franquia DO RESULTADO, não a do filtro antes de buscar.
const franchiseBuscada = ref<number | null>(null);
const relatorio = ref<RelatorioCombosUnidade | null>(null);
const erro = ref<string | null>(null);

const moeda = (valor?: number): string => formatarMoeda(valor ?? 0, franchiseBuscada.value);

async function buscar(): Promise<void> {
  if (!franchiseId.value) {
    erro.value = t('Escolha a franquia para ver o relatório.');
    relatorio.value = null;
    return;
  }
  erro.value = null;
  const franquia = Number(franchiseId.value);
  try {
    relatorio.value = await fetchRelatorioUnidade({
      franquia_id: franquia,
      data_inicio: dataInicio.value,
      data_fim: dataFim.value,
    });
    franchiseBuscada.value = franquia;
  } catch (err) {
    relatorio.value = null;
    erro.value = err instanceof Error ? err.message : t('Erro ao carregar o relatório de combos');
  }
}

// A franquia chega sozinha do FranchiseSelect: a tela abre com o mês corrente dela.
watch(franchiseId, (id) => {
  if (id) void buscar();
});

const combosConfig = computed(() => {
  const dinheiro = (campo: 'valor_vendido' | 'valor_cancelado') => ({ data: row }: { data: Record<string, unknown> }): string =>
    moeda(row[campo] as number);

  return {
    tableId: 'tabelaRelatorioCombos',
    filename: `relatorio_combos_${new Date().toISOString().split('T')[0]}`,
    columns: [
      { data: 'combo_nome', title: t('Combo'), sortable: true },
      { data: 'vendidos', title: t('Vendidos'), width: 90, sortable: true, renderType: 'integer', explanation: EX.value.vendidos },
      { data: 'valor_vendido', title: t('Valor vendido'), width: 130, sortable: true, calculated: true, explanation: EX.value.valorVendido, cellRenderer: dinheiro('valor_vendido') },
      { data: 'cancelados', title: t('Cancelados'), width: 100, sortable: true, renderType: 'integer', explanation: EX.value.cancelados },
      { data: 'valor_cancelado', title: t('Valor cancelado'), width: 130, sortable: true, calculated: true, explanation: EX.value.valorCancelado, cellRenderer: dinheiro('valor_cancelado') },
      { data: 'pares_usados', title: t('Pares usados'), width: 100, sortable: true, renderType: 'integer', explanation: EX.value.paresUsados },
      { data: 'estornos', title: t('Estornos'), width: 90, sortable: true, renderType: 'integer', explanation: EX.value.estornos },
      { data: 'ajustes_credito', title: t('Ajustes (+)'), width: 100, sortable: true, renderType: 'integer', explanation: EX.value.ajustes },
      { data: 'ajustes_debito', title: t('Ajustes (−)'), width: 100, sortable: true, renderType: 'integer', explanation: EX.value.ajustes },
      { data: 'pares_cancelados', title: t('Pares cancelados'), width: 110, sortable: true, renderType: 'integer', explanation: EX.value.paresCancelados },
      { data: 'compras_ativas', title: t('Compras ativas'), width: 110, sortable: true, renderType: 'integer', explanation: EX.value.comprasAtivas },
      { data: 'saldo_disponivel', title: t('Saldo disponível'), width: 110, sortable: true, renderType: 'integer', explanation: EX.value.saldoDisponivel },
      { data: 'saldo_reservado', title: t('Saldo reservado'), width: 110, sortable: true, renderType: 'integer', explanation: EX.value.saldoReservado },
      { data: 'saldo_vencido', title: t('Saldo vencido'), width: 110, sortable: true, renderType: 'integer', explanation: EX.value.saldoVencido },
    ],
    pagination: false,
    sortable: true,
    showFilters: false,
    heightMode: 'auto',
    stripedRows: true,
    showGridlines: true,
    size: 'small',
  };
});

defineExpose({ buscar });
</script>

<style scoped>
.rc-filtros {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.rc-totais {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.rc-aviso {
  padding: 1rem;
}
</style>
